import axios from "axios";

import { BASE_URL } from "./api";

export async function getDepartments(token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/back-office/departments/`,
        headers: {
            'Authorization': `Token ${token}`
        },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function getWorkItems(token, params = {}) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/back-office/work-items/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        params: params,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function getDashboard(token, params = {}) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/back-office/dashboard/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        params: params,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function markTaskPeriod(token, payload) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/back-office/dashboard/mark/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        data: payload,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function getRecurringTasks(token, params = {}) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/back-office/recurring-tasks/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        params: params,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function getSprints(token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/back-office/sprints/`,
        headers: {
            'Authorization': `Token ${token}`
        },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// The sprint calendar is reference data for the whole Back Office, so these
// three are the only writes to it anywhere in the client -- and the server
// admits them from dev_group alone, whatever the menu happens to show.
export async function createSprint(token, payload) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/back-office/sprints/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        data: payload,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function updateSprint(token, id, payload) {
    return axios({
        method: 'patch',
        url: `${BASE_URL}/back-office/sprints/${id}/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        data: payload,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// Answers 409 when work items still point at the sprint, with a message
// naming how many. The caller shows that rather than inventing its own.
export async function deleteSprint(token, id) {
    return axios({
        method: 'delete',
        url: `${BASE_URL}/back-office/sprints/${id}/`,
        headers: {
            'Authorization': `Token ${token}`
        },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function createWorkItem(token, payload) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/back-office/work-items/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        data: payload,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// Archive, not delete: the row stays, with a date on it saying when it was
// put away. Nothing in a backlog is worth losing the history of.
export async function archiveWorkItem(token, id) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/back-office/work-items/${id}/archive/`,
        headers: {
            'Authorization': `Token ${token}`
        },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// Takes every item still at Backlog off whatever sprint week it is sitting
// in. With { dryRun: true } it counts them and writes nothing, which is how
// the board can say how many it is about to move before it asks.
export async function clearBacklogSprints(token, { dryRun = false } = {}) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/back-office/work-items/clear-backlog-sprints/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        data: { dry_run: dryRun },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// The change log for one backlog item: who changed what, when, plus the
// dated fields pulled out into their own group. Derived on the server from
// the snapshots simple_history writes, so there is nothing to keep in step
// here -- an edit made anywhere shows up in this the next time it is read.
export async function getWorkItemHistory(token, id) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/back-office/work-items/${id}/history/`,
        headers: {
            'Authorization': `Token ${token}`
        },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function updateWorkItem(token, id, payload) {
    return axios({
        method: 'patch',
        url: `${BASE_URL}/back-office/work-items/${id}/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        data: payload,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// `onBehalfOf` is a user id, for the team mirroring a request that arrived
// over Slack: the request is filed under that person's name and shows up on
// their page. The server refuses it for anyone not on the back-office team,
// rather than quietly filing under the caller -- see can_act_on_behalf.
export async function submitRequest(token, body, onBehalfOf = null) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/back-office/intake/submit/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        data: onBehalfOf ? { body: body, on_behalf_of: onBehalfOf } : { body: body },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// `asUser` reads the page as that person sees it. Exposes nothing new: the
// callers allowed to pass it can already read every request through the
// triage surface.
export async function getMyRequests(token, asUser = null) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/back-office/intake/my-requests/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        params: asUser ? { as_user: asUser } : {},
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function getAttachments(token, targetType, targetId) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/back-office/attachments/`,
        params: { target_type: targetType, target_id: targetId },
        headers: {
            'Authorization': `Token ${token}`
        },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function uploadAttachment(token, targetType, targetId, file, caption) {
    const form = new FormData()
    form.append('target_type', targetType)
    form.append('target_id', targetId)
    form.append('file', file)
    if (caption) form.append('caption', caption)
    return axios({
        method: 'post',
        url: `${BASE_URL}/back-office/attachments/`,
        data: form,
        // No Content-Type here on purpose: axios has to set it so the
        // multipart boundary matches the body it generates.
        headers: {
            'Authorization': `Token ${token}`
        },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function deleteAttachment(token, attachmentId) {
    return axios({
        method: 'delete',
        url: `${BASE_URL}/back-office/attachments/${attachmentId}/`,
        headers: {
            'Authorization': `Token ${token}`
        },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function getRequestBacklog(token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/back-office/intake/request-backlog/`,
        headers: {
            'Authorization': `Token ${token}`
        },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function getSharedRoadmap(token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/back-office/intake/roadmap/`,
        headers: {
            'Authorization': `Token ${token}`
        },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function getRequests(token, params = {}) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/back-office/requests/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        params: params,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function updateRequest(token, id, payload) {
    return axios({
        method: 'patch',
        url: `${BASE_URL}/back-office/requests/${id}/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        data: payload,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function getBlockers(token, params = {}) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/back-office/blockers/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        params: params,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function createBlocker(token, payload) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/back-office/blockers/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        data: payload,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function updateBlocker(token, id, payload) {
    return axios({
        method: 'patch',
        url: `${BASE_URL}/back-office/blockers/${id}/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        data: payload,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function getPosts(token, params = {}) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/back-office/posts/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        params: params,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function createPost(token, payload) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/back-office/posts/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        data: payload,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function updatePost(token, id, payload) {
    return axios({
        method: 'patch',
        url: `${BASE_URL}/back-office/posts/${id}/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        data: payload,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// Creates the backlog item AND links it to the request in one call. Doing it
// as two (POST the item, PATCH the request) can leave an orphan item in the
// backlog if the second half fails.
export async function createWorkItemForRequest(token, id, payload) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/back-office/requests/${id}/create-work-item/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        data: payload,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// Whether this caller may file or answer as somebody else, and the people
// they may do it for. Safe to call for anyone: a caller with no such
// permission gets {can_act_on_behalf: false, people: []} rather than a 403,
// so the page renders either way.
export async function getStandInOptions(token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/back-office/intake/stand-in-options/`,
        headers: {
            'Authorization': `Token ${token}`
        },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// The reporter answering a 'Need More Information' request. Lives on the
// intake surface, not the triage one -- it is the only write a reporter has
// besides submitting.
export async function replyToRequest(token, id, body, onBehalfOf = null) {
    const data = { request: id, body: body }
    if (onBehalfOf) data.on_behalf_of = onBehalfOf
    return axios({
        method: 'post',
        url: `${BASE_URL}/back-office/intake/reply/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        data: data,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}
